import { memo, useCallback } from 'react';
import { useCanvasStores } from '../stores/CanvasContext';
import { useCanvasActions } from '../hooks/useCanvasActions';
import { useResourceById } from '../../stores/hooks';
import { formatFieldValue } from '../../lib/formatFieldValue';

interface ResourceChipProps {
  entityId: string;
  resourceId: string;
}

const ResourceChip = memo(function ResourceChip({ entityId, resourceId }: ResourceChipProps) {
  const resource = useResourceById(resourceId);
  const { uiStore } = useCanvasStores();
  const { handleDetachResource } = useCanvasActions();

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    (uiStore as any).send({ type: 'resourceDetail.open', resourceId });
  }, [uiStore, resourceId]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleDetachResource(entityId, resourceId);
  }, [handleDetachResource, entityId, resourceId]);

  if (!resource) return null;

  const color = resource.color || '#6366f1';
  const primaryField = resource.fields.find(f => f.showOnBadge);
  const chipText = primaryField ? formatFieldValue(primaryField) : resource.name;

  return (
    <div
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onPointerDown={(e) => e.stopPropagation()}
      title={`${resource.name}${primaryField ? ' \u00b7 ' + formatFieldValue(primaryField) : ''}\nRight-click to detach`}
      style={{
        height: 14,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '0 4px',
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 4,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {/* Color dot */}
      <span
        style={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      {/* Value text */}
      <span style={{ fontSize: 8, color: '#374151', fontWeight: 500, lineHeight: 1 }}>
        {chipText}
      </span>
    </div>
  );
});

export interface ResourceChipOverlayProps {
  entityId: string;
  entityX: number;
  entityY: number;
  entityWidth: number;
  resourceIds: string[];
}

export const ResourceChipOverlay = memo(function ResourceChipOverlay({
  entityId,
  entityX,
  entityY,
  entityWidth,
  resourceIds,
}: ResourceChipOverlayProps) {
  return (
    <div
      data-resource-chips={entityId}
      style={{
        position: 'absolute',
        right: 'auto',
        left: entityX,
        top: entityY - 18,
        width: entityWidth,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
        pointerEvents: 'auto',
        zIndex: 5,
      }}
    >
      {resourceIds.map((rid) => (
        <ResourceChip key={rid} entityId={entityId} resourceId={rid} />
      ))}
    </div>
  );
});
